'use client';

import {
	Activity,
	AlertTriangle,
	BarChart2,
	CheckCircle2,
	Euro,
	FileText,
	Globe,
	Package,
	Shield,
	SlidersHorizontal,
	Sun,
	TrendingUp,
	Users,
	Wrench,
	Zap,
} from 'lucide-react';
import * as React from 'react';
import type { SidebarNavGroup } from '../sidebar-navigation/sidebar-navigation';
import type { ProModeId } from './pro-shell';

/** Id delle sezioni PRO — coincidono con i segmenti di rotta dell'app. */
export type ProSectionId =
	| 'fleet'
	| 'single'
	| 'field'
	| 'assets'
	| 'topology'
	| 'inventory'
	| 'monitor'
	| 'forecast'
	| 'design'
	| 'docs'
	| 'anomaly'
	| 'solutions'
	| 'predictive'
	| 'oman'
	| 'rendiconto'
	| 'warranty'
	| 'roles'
	| 'reports'
	| 'config';

/** Le 19 sezioni in ordine di registro (lo stesso di `MODULES` nel backend). */
export const PRO_SECTION_IDS: readonly ProSectionId[] = [
	'fleet',
	'single',
	'field',
	'assets',
	'topology',
	'inventory',
	'monitor',
	'forecast',
	'design',
	'docs',
	'anomaly',
	'solutions',
	'predictive',
	'oman',
	'rendiconto',
	'warranty',
	'roles',
	'reports',
	'config',
];

export interface ProNavCounts {
	anomalies?: number;
	tickets?: number;
}

const DASHBOARD_SECTION: Record<ProModeId, ProSectionId> = {
	fleet: 'fleet',
	single: 'single',
	field: 'field',
};

const DASHBOARD_GROUP_LABEL: Record<ProModeId, string> = {
	fleet: 'Fleet Multi-Sito',
	single: 'Impianto Singolo',
	field: 'Campo Utility-Scale',
};

const icon = (C: React.ComponentType<{ className?: string }>) => <C className="h-4 w-4" />;

/** Sezione dashboard attiva per la modalità header corrente. */
export function proDashboardSection(mode: ProModeId): ProSectionId {
	return DASHBOARD_SECTION[mode];
}

/**
 * Struttura di navigazione della sidebar PRO (dal mock v4.3). Il primo gruppo
 * punta alla dashboard della modalità corrente; i badge arrivano dal wiring.
 * Con `enabled` restano solo le voci incluse, e i gruppi rimasti vuoti spariscono.
 */
export function buildProNavGroups(
	mode: ProModeId,
	dashboardLabel: string | null,
	counts: ProNavCounts = {},
	enabled?: ReadonlySet<ProSectionId>,
): SidebarNavGroup[] {
	const groups: SidebarNavGroup[] = [
		{
			label: DASHBOARD_GROUP_LABEL[mode],
			items: [
				{
					id: DASHBOARD_SECTION[mode],
					label: dashboardLabel ?? 'Dashboard',
					icon: icon(Globe),
				},
			],
		},
		{
			label: 'Impianti',
			items: [
				{ id: 'assets', label: 'Asset & Impianti', icon: icon(Wrench) },
				{ id: 'topology', label: 'Cabina Primaria', icon: icon(Zap) },
				{ id: 'inventory', label: 'Inventario Ricambi', icon: icon(Package) },
			],
		},
		{
			label: 'Produzione',
			items: [
				{ id: 'monitor', label: 'Monitoraggio Energia', icon: icon(BarChart2) },
				{ id: 'forecast', label: 'Previsione', icon: icon(Sun) },
			],
		},
		{
			label: 'Progettazione',
			items: [
				{ id: 'design', label: 'Dimensionamento FV', icon: icon(SlidersHorizontal) },
				{ id: 'docs', label: 'Pratiche GSE/STMG', icon: icon(FileText) },
			],
		},
		{
			label: 'Manutenzione',
			items: [
				{
					id: 'anomaly',
					label: 'Rilevamento Anomalie AI',
					icon: icon(AlertTriangle),
					badge: counts.anomalies ? { text: String(counts.anomalies), tone: 'red' } : null,
				},
				{
					id: 'solutions',
					label: 'Soluzioni Operative',
					icon: icon(CheckCircle2),
					badge: counts.tickets ? { text: String(counts.tickets), tone: 'amber' } : null,
				},
				{ id: 'predictive', label: 'Manutenzione Predittiva', icon: icon(Activity) },
			],
		},
		{
			label: 'Business',
			items: [
				{ id: 'oman', label: 'Economica O&M', icon: icon(TrendingUp) },
				{ id: 'rendiconto', label: 'Rendiconto Socio/Cliente', icon: icon(Euro) },
				{ id: 'warranty', label: 'Garanzie & Compliance', icon: icon(Shield) },
				{ id: 'roles', label: 'Accessi & Ruoli', icon: icon(Users) },
			],
		},
	];
	if (!enabled) return groups;
	return groups
		.map((g) => ({ ...g, items: g.items.filter((i) => enabled.has(i.id as ProSectionId)) }))
		.filter((g) => g.items.length > 0);
}
